import { memo, useMemo } from 'react';
import * as THREE from 'three';
import type { computeVoronoiCells } from '@4cm/core/tactics/overlays';

/**
 * 3D version of the Spacing overlay — per-player tinted polygons.
 *
 * Coordinate convention: cell vertices arrive in world `{x, z}`
 * metres. Three.js wants Vector2s for `THREE.Shape`; we build in
 * (X, Y) and rotate -π/2 around X to drop the result onto the
 * pitch's XZ ground plane.
 */
export type VoronoiLayer3DCells = ReturnType<typeof computeVoronoiCells>;

export interface VoronoiLayer3DProps {
  cells: VoronoiLayer3DCells;
  /** Opacity in [0, 1]. */
  opacity: number;
  /** Hides away-team cells when false (paired with the master opponent
   *  toggle). Default true. */
  showAway?: boolean;
}

// Home green / away amber — matches PlayerToken jersey colours and
// the 2D VoronoiLayer fills.
const HOME_HEX = 0x4ade80;
const AWAY_HEX = 0xfbbf24;
const NEUTRAL_HEX = 0x4ade80;

function colorFor(team: 'home' | 'away' | undefined): number {
  if (team === 'away') return AWAY_HEX;
  return team === 'home' ? HOME_HEX : NEUTRAL_HEX;
}

function VoronoiLayer3DInner({ cells, opacity, showAway = true }: VoronoiLayer3DProps) {
  const items = useMemo(() => {
    if (!cells || cells.length === 0) return [];
    const out: Array<{ key: number; geom: THREE.ShapeGeometry; color: number }> = [];
    cells.forEach((cell, idx) => {
      if (!cell.vertices || cell.vertices.length < 3) return;
      if (!showAway && cell.team === 'away') return;
      // World (x, z) → Shape (X, Y) where X = x, Y = -z. The group is
      // then rotated -π/2 around X so the Shape's Y becomes world Z,
      // which means the negation cancels out and the cell lands at the
      // right world position.
      const shape = new THREE.Shape();
      const v0 = cell.vertices[0]!;
      shape.moveTo(v0.x, -v0.z);
      for (let i = 1; i < cell.vertices.length; i++) {
        const v = cell.vertices[i]!;
        shape.lineTo(v.x, -v.z);
      }
      shape.closePath();
      out.push({
        key: idx,
        geom: new THREE.ShapeGeometry(shape),
        color: colorFor(cell.team),
      });
    });
    return out;
  }, [cells, showAway]);

  if (items.length === 0) return null;

  return (
    <group
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.005, 0]}
      raycast={() => undefined}
      renderOrder={1}
    >
      {items.map(({ key, geom, color }) => (
        <mesh key={key} geometry={geom}>
          <meshBasicMaterial
            color={color}
            transparent
            opacity={opacity * 0.65}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

export const VoronoiLayer3D = memo(VoronoiLayer3DInner);
